/**
 * Student meal plans — weekly booking admin (POS).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  Customer,
  MealPeriod,
  MealPlanCostSettings,
  MealPlanDayMenu,
  MealPlanExceptionRequest,
  MealPlanSubscription,
  MealPlanType,
  MealPlanWeek,
  MenuItem,
  WeeklyBooking,
  Category,
} from '../../types';
import {
  autoBookSubscriptionForWeek,
  mealPlanCostSettingsService,
  mealPlanExceptionsService,
  mealPlanSubscriptionsService,
  mealPlanTcVersionsService,
  mealPlanTypesService,
  mealPlanWeeksService,
  weeklyBookingsService,
} from '../../lib/database';
import {
  buildPrepReport,
  computeMealPlanCostAnalysis,
  summarizeWeekBookings,
} from '../../lib/mealPlanBooking';
import { DEFAULT_MEAL_PLAN_TC_BODY } from '../../lib/mealPlanTc';
import MealPlanNewCustomerForm from './MealPlanNewCustomerForm';
import {
  addDaysToDayKey,
  cancellationCutoffMs,
  dayKeysForWeek,
  dayMenuAsText,
  mealPeriodText,
  nextWeekId,
  offeredPeriods,
  weekBoundsMs,
  weekIdContaining,
} from '../../lib/mealPlanWeek';
import PlusIcon from '../icons/PlusIcon';

const PERIODS: MealPeriod[] = ['breakfast', 'lunch', 'dinner'];
const WEEKDAYS = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
  { n: 6, label: 'Sat' },
  { n: 7, label: 'Sun' },
];

const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MEAL_PLACEHOLDER = 'Steamed rice, chicken curry, seasonal vegetable thoran, dal and pickle.';

function dollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

function formatDayLabel(dayKey: string, index: number): string {
  const [, m, d] = dayKey.split('-');
  return `${DAY_SHORT[index]} ${Number(d)}/${Number(m)}`;
}

function formatDayHeading(dayKey: string, index: number): string {
  const [, month, date] = dayKey.split('-');
  return `${DAY_LONG[index]} ${Number(date)} ${MONTH_SHORT[Number(month) - 1] || ''}`.trim();
}

function formatWeekRange(weekId: string): string {
  const days = dayKeysForWeek(weekId);
  const start = formatDayLabel(days[0], 0);
  const end = formatDayLabel(days[6], 6);
  return `${start} – ${end}`;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'published':
      return 'bg-green-100 text-green-800';
    case 'locked':
      return 'bg-amber-100 text-amber-900';
    case 'closed':
      return 'bg-gray-200 text-gray-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function paymentSourceLabel(source?: string): string {
  if (source === 'pos') return 'Paid on the till';
  if (source === 'online') return 'Paid on phone or website';
  if (source === 'complimentary') return 'No payment';
  return 'Payment not recorded';
}

function formatRecordedAt(ms?: number): string {
  if (!ms) return 'time not recorded';
  return new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(ms);
}

function bookingDayLabel(dayKey: string): string {
  const [year, month, date] = dayKey.split('-').map(Number);
  if (!year || !month || !date) return dayKey;
  const weekday = new Date(Date.UTC(year, month - 1, date)).getUTCDay();
  return `${DAY_SHORT[weekday === 0 ? 6 : weekday - 1] || ''} ${date}/${month}`.trim();
}

function planScheduleLabel(plan: MealPlanType | undefined): string {
  if (!plan) return '';
  const days = plan.daysOfWeek.map((n) => DAY_SHORT[n - 1] || String(n)).join(' ');
  const meals = plan.periods.map((p) => p[0].toUpperCase() + p.slice(1)).join(', ');
  return `${meals} · ${days}`;
}

function emptyType(): MealPlanType {
  return {
    id: '',
    name: '',
    description: '',
    active: true,
    bookingMode: 'auto',
    periods: ['lunch'],
    daysOfWeek: [1, 2, 3, 4, 5],
    priceCents: 5000,
    tcVersionRef: '2026-v1',
    sortOrder: 0,
  };
}

interface MealPlansTabProps {
  customers: Customer[];
  menuItems: MenuItem[];
  categories: Category[];
  currentStaffId?: number | string;
  currentStaffName?: string;
  isManager?: boolean;
  /** Floor till hides the free grant. Settings can still pass true. */
  allowManualGrant?: boolean;
  onCustomerCreated?: (customer: Customer) => void;
}

const MealPlansTab: React.FC<MealPlansTabProps> = ({
  customers,
  currentStaffId,
  currentStaffName,
  isManager = true,
  allowManualGrant = true,
  onCustomerCreated,
}) => {
  const [subTab, setSubTab] = useState<
    'weeks' | 'types' | 'subscribers' | 'exceptions' | 'cost'
  >('weeks');
  const [types, setTypes] = useState<MealPlanType[]>([]);
  const [weeks, setWeeks] = useState<MealPlanWeek[]>([]);
  const [selectedWeekId, setSelectedWeekId] = useState(nextWeekId());
  const [weekDoc, setWeekDoc] = useState<MealPlanWeek | null>(null);
  const [bookings, setBookings] = useState<WeeklyBooking[]>([]);
  const [subs, setSubs] = useState<MealPlanSubscription[]>([]);
  const [exceptions, setExceptions] = useState<MealPlanExceptionRequest[]>([]);
  const [typeDraft, setTypeDraft] = useState<MealPlanType | null>(null);
  const [priceText, setPriceText] = useState('');
  const [tcBodyDraft, setTcBodyDraft] = useState(DEFAULT_MEAL_PLAN_TC_BODY);
  const [tcBodyLoading, setTcBodyLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [costSettings, setCostSettings] = useState<MealPlanCostSettings | null>(null);
  const [grantCustomerId, setGrantCustomerId] = useState('');
  const [grantPlanTypeId, setGrantPlanTypeId] = useState('');
  const [lookupQuery, setLookupQuery] = useState('');
  const [menuDirty, setMenuDirty] = useState(false);
  const [savedMenuJson, setSavedMenuJson] = useState('');
  const [addedCustomers, setAddedCustomers] = useState<Customer[]>([]);
  const [bookedQuery, setBookedQuery] = useState('');
  const [subscriberBookings, setSubscriberBookings] = useState<WeeklyBooking[]>([]);

  const refreshTypes = useCallback(async () => {
    setTypes(await mealPlanTypesService.getAll());
  }, []);

  const refreshWeeks = useCallback(async () => {
    setWeeks(await mealPlanWeeksService.getAll());
  }, []);

  const loadWeek = useCallback(async (weekId: string) => {
    setBusy(true);
    try {
      const w = await mealPlanWeeksService.ensureDraft(weekId);
      setWeekDoc(w);
      setSavedMenuJson(JSON.stringify(w.menu || {}));
      setMenuDirty(false);
      setBookings(await weeklyBookingsService.getByWeekId(weekId));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void refreshTypes();
    void refreshWeeks();
    void mealPlanSubscriptionsService.getAll().then(setSubs);
    void mealPlanExceptionsService.getAll().then(setExceptions);
    void mealPlanCostSettingsService.get().then(setCostSettings);
  }, [refreshTypes, refreshWeeks]);

  useEffect(() => {
    void loadWeek(selectedWeekId);
  }, [selectedWeekId, loadWeek]);

  useEffect(() => {
    const weekIds = [...new Set(subs.map((s) => s.coveredWeekId).filter((id): id is string => Boolean(id)))];
    if (weekIds.length === 0) {
      setSubscriberBookings([]);
      return;
    }
    let cancelled = false;
    void Promise.all(weekIds.map((id) => weeklyBookingsService.getByWeekId(id))).then((groups) => {
      if (!cancelled) setSubscriberBookings(groups.flat());
    });
    return () => {
      cancelled = true;
    };
  }, [subs]);

  const knownCustomers = useMemo(() => {
    const byId = new Map<string, Customer>();
    for (const customer of [...customers, ...addedCustomers]) byId.set(String(customer.id), customer);
    return [...byId.values()];
  }, [customers, addedCustomers]);

  const prepRows = useMemo(() => {
    if (!weekDoc) return [];
    return buildPrepReport(weekDoc.id, bookings, weekDoc.menu || {});
  }, [weekDoc, bookings]);

  const weekStats = useMemo(() => summarizeWeekBookings(bookings), [bookings]);

  const filteredCustomers = useMemo(() => {
    const q = lookupQuery.trim().toLowerCase();
    if (!q) return [];
    return knownCustomers
      .filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.phone.includes(q),
      )
      .slice(0, 8);
  }, [knownCustomers, lookupQuery]);

  const openTypeDraft = async (draft: MealPlanType) => {
    setTypeDraft(draft);
    setPriceText(dollars(draft.priceCents));
    setTcBodyLoading(true);
    try {
      const row = await mealPlanTcVersionsService.get(draft.tcVersionRef || '2026-v1');
      setTcBodyDraft(row?.body?.trim() || DEFAULT_MEAL_PLAN_TC_BODY);
    } catch {
      setTcBodyDraft(DEFAULT_MEAL_PLAN_TC_BODY);
    } finally {
      setTcBodyLoading(false);
    }
  };

  const loadTcBodyForVersion = async (version: string) => {
    const id = version.trim() || '2026-v1';
    setTcBodyLoading(true);
    try {
      const row = await mealPlanTcVersionsService.get(id);
      setTcBodyDraft(row?.body?.trim() || DEFAULT_MEAL_PLAN_TC_BODY);
    } catch {
      setTcBodyDraft(DEFAULT_MEAL_PLAN_TC_BODY);
    } finally {
      setTcBodyLoading(false);
    }
  };

  const saveType = async () => {
    if (!typeDraft?.name.trim()) {
      alert('Plan name is required.');
      return;
    }
    const tcVersionRef = (typeDraft.tcVersionRef || '2026-v1').trim() || '2026-v1';
    const priceCents = Math.round((parseFloat(priceText) || 0) * 100);
    await mealPlanTypesService.save({
      ...typeDraft,
      id: typeDraft.id || `mpt-${Date.now()}`,
      name: typeDraft.name.trim(),
      tcVersionRef,
      priceCents,
    });
    await mealPlanTcVersionsService.save(tcVersionRef, tcBodyDraft.trim() || DEFAULT_MEAL_PLAN_TC_BODY);
    setTypeDraft(null);
    await refreshTypes();
  };

  const menuWithoutDishPrices = (menu: MealPlanWeek['menu']) => {
    const next: MealPlanWeek['menu'] = {};
    for (const [dayKey, day] of Object.entries(menu || {})) {
      next[dayKey] = dayMenuAsText(day);
    }
    return next;
  };

  const saveWeekMenu = async () => {
    if (!weekDoc) return;
    const saved = await mealPlanWeeksService.save({
      ...weekDoc,
      menu: menuWithoutDishPrices(weekDoc.menu),
    });
    setWeekDoc(saved);
    setSavedMenuJson(JSON.stringify(saved.menu || {}));
    setMenuDirty(false);
    await refreshWeeks();
  };

  const publishWeek = async () => {
    if (!weekDoc) return;
    const menu = menuWithoutDishPrices(weekDoc.menu);
    if (menuDirty) {
      const ok = window.confirm('You have unsaved menu changes. Save and publish?');
      if (!ok) return;
    }
    const saved = await mealPlanWeeksService.save({
      ...weekDoc,
      menu,
      status: 'published',
      publishedAt: Date.now(),
    });
    setWeekDoc(saved);
    setSavedMenuJson(JSON.stringify(saved.menu || {}));
    setMenuDirty(false);
    await refreshWeeks();
  };

  const lockWeek = async () => {
    if (!weekDoc) return;
    if (!window.confirm('Lock this week? Students can no longer cancel bookings.')) return;
    const now = Date.now();
    await mealPlanWeeksService.save({
      ...weekDoc,
      status: 'locked',
      lockedAt: now,
    });
    const rows = await weeklyBookingsService.getByWeekId(weekDoc.id);
    for (const b of rows) {
      if (b.status === 'booked' || b.status === 'cancelled') {
        await weeklyBookingsService.save({
          ...b,
          lockedAt: now,
          updatedAt: now,
        });
      }
    }
    await loadWeek(weekDoc.id);
  };

  const copyFromPrevious = async () => {
    const fromWeekId = addDaysToDayKey(selectedWeekId, -7);
    if (!window.confirm(`Copy menu from week of ${formatWeekRange(fromWeekId)} into this week?`)) {
      return;
    }
    setBusy(true);
    try {
      const source = await mealPlanWeeksService.getById(fromWeekId);
      if (!source) {
        alert(`No saved menu found for ${fromWeekId}. Publish or save that week first.`);
        return;
      }
      const copied = await mealPlanWeeksService.copyWeek(fromWeekId, selectedWeekId);
      setWeekDoc(copied);
      setSavedMenuJson(JSON.stringify(copied.menu || {}));
      setMenuDirty(false);
      await refreshWeeks();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Copy failed.');
    } finally {
      setBusy(false);
    }
  };

  const runAutoBook = async () => {
    if (!weekDoc || weekDoc.status === 'draft') {
      alert('Publish the week before auto-booking.');
      return;
    }
    setBusy(true);
    try {
      let total = 0;
      const activeTypes = types.filter((t) => t.active && t.bookingMode === 'auto');
      const activeSubs = subs.filter((s) => s.status === 'active');
      for (const sub of activeSubs) {
        const plan = activeTypes.find((t) => t.id === sub.planTypeId) || types.find((t) => t.id === sub.planTypeId);
        if (!plan || plan.bookingMode !== 'auto') continue;
        total += await autoBookSubscriptionForWeek(sub, plan, weekDoc.id);
      }
      await loadWeek(weekDoc.id);
      alert(`Auto-book created ${total} booking row(s).`);
    } finally {
      setBusy(false);
    }
  };

  const grantSubscription = async () => {
    const customer = customers.find((c) => String(c.id) === grantCustomerId);
    const plan = types.find((t) => t.id === grantPlanTypeId);
    if (!customer) {
      alert('Select a customer.');
      return;
    }
    if (!plan) {
      alert('Select a plan type.');
      return;
    }
    const now = Date.now();
    const coveredWeekId = weekDoc?.id || nextWeekId();
    const sub = await mealPlanSubscriptionsService.save({
      id: `mps-${Date.now()}`,
      customerId: String(customer.id),
      customerEmail: (customer.email || '').trim().toLowerCase(),
      planTypeId: plan.id,
      planTypeName: plan.name,
      status: 'active',
      activeFrom: now,
      activeTo: weekBoundsMs(coveredWeekId).weekEndMs,
      coveredWeekId,
      paymentSource: 'complimentary',
      createdAt: now,
      updatedAt: now,
      createdByStaffId: currentStaffId,
    });
    setSubs(await mealPlanSubscriptionsService.getAll());
    if (plan.bookingMode === 'auto' && weekDoc && weekDoc.status !== 'draft') {
      await autoBookSubscriptionForWeek(sub, plan, weekDoc.id);
      await loadWeek(weekDoc.id);
    }
    alert('Subscription granted.');
  };

  const patchDay = (dayKey: string, patch: Partial<MealPlanDayMenu>) => {
    if (!weekDoc) return;
    const day: MealPlanDayMenu = weekDoc.menu[dayKey] || {
      breakfast: [],
      lunch: [],
      dinner: [],
    };
    const nextMenu = {
      ...weekDoc.menu,
      [dayKey]: { ...day, ...patch },
    };
    setWeekDoc({ ...weekDoc, menu: nextMenu });
    setMenuDirty(JSON.stringify(nextMenu) !== savedMenuJson);
  };

  const [copyDayKey, setCopyDayKey] = useState<string | null>(null);
  const [copyDayTargets, setCopyDayTargets] = useState<string[]>([]);

  const copyPeriodAcrossWeek = (sourceDayKey: string, period: MealPeriod) => {
    if (!weekDoc) return;
    const text = mealPeriodText(weekDoc.menu[sourceDayKey], period);
    if (!text) {
      alert('That meal has nothing to copy.');
      return;
    }
    const textKey = `${period}Text` as const;
    const nextMenu = { ...weekDoc.menu };
    for (const dayKey of dayKeysForWeek(weekDoc.id)) {
      const day = nextMenu[dayKey] || { breakfast: [], lunch: [], dinner: [] };
      nextMenu[dayKey] = {
        ...day,
        [textKey]: text,
        [period]: [],
      };
    }
    setWeekDoc({ ...weekDoc, menu: nextMenu });
    setMenuDirty(JSON.stringify(nextMenu) !== savedMenuJson);
  };

  const dayHasDishes = (dayKey: string) => {
    const day = weekDoc?.menu[dayKey];
    if ((day?.title || '').trim()) return true;
    return PERIODS.some((period) => mealPeriodText(day, period).length > 0);
  };

  const copyDayOnto = (sourceDayKey: string, targetDayKeys: string[]) => {
    if (!weekDoc || weekReadOnly) return;
    const source = weekDoc.menu[sourceDayKey] || { breakfast: [], lunch: [], dinner: [] };
    if (!dayHasDishes(sourceDayKey)) {
      alert('That day has no meals to copy.');
      return;
    }
    const targets = targetDayKeys.filter((dayKey) => dayKey !== sourceDayKey);
    if (targets.length === 0) return;
    const nextMenu = { ...weekDoc.menu };
    const copied = dayMenuAsText(source);
    for (const dayKey of targets) {
      nextMenu[dayKey] = copied;
    }
    setWeekDoc({ ...weekDoc, menu: nextMenu });
    setMenuDirty(JSON.stringify(nextMenu) !== savedMenuJson);
    setCopyDayKey(null);
    setCopyDayTargets([]);
  };

  const menuFillStats = useMemo(() => {
    if (!weekDoc) return { filled: 0, total: 0 };
    const days = dayKeysForWeek(weekDoc.id);
    const periods = offeredPeriods(weekDoc.periods);
    let filled = 0;
    const total = days.length * periods.length;
    for (const dayKey of days) {
      for (const period of periods) {
        if (mealPeriodText(weekDoc.menu[dayKey], period)) filled += 1;
      }
    }
    return { filled, total };
  }, [weekDoc]);

  const weekReadOnly = weekDoc?.status === 'locked' || weekDoc?.status === 'closed';

  const resolveException = async (
    req: MealPlanExceptionRequest,
    status: 'approved' | 'denied',
    comment: string,
  ) => {
    if (!isManager) {
      alert('Managers only.');
      return;
    }
    await mealPlanExceptionsService.save({
      ...req,
      status,
      managerComment: comment,
      managerId: currentStaffId,
      managerName: currentStaffName,
      resolvedAt: Date.now(),
      updatedAt: Date.now(),
    });
    if (status === 'approved') {
      // Carry unused booked → cancel from fromWeek, create booked on toWeek same periods
      for (const bookingId of req.bookingIds) {
        const allFrom = await weeklyBookingsService.getByWeekId(req.fromWeekId);
        const b = allFrom.find((x) => x.id === bookingId);
        if (!b || b.status !== 'booked') continue;
        await weeklyBookingsService.save({ ...b, status: 'cancelled', updatedAt: Date.now() });
        const newId = `wb-${b.customerId}-${b.dayKey.replace(req.fromWeekId, '')}-${b.period}-carry`;
        // Place on same weekday in toWeek
        const fromDays = dayKeysForWeek(req.fromWeekId);
        const toDays = dayKeysForWeek(req.toWeekId);
        const idx = fromDays.indexOf(b.dayKey);
        const toDay = idx >= 0 ? toDays[idx] : toDays[0];
        await weeklyBookingsService.save({
          ...b,
          id: `wb-${b.customerId}-${toDay}-${b.period}`,
          weekId: req.toWeekId,
          dayKey: toDay,
          status: 'booked',
          lockedAt: undefined,
          redeemedAt: undefined,
          redeemedOrderId: undefined,
          updatedAt: Date.now(),
          createdAt: Date.now(),
        });
        void newId;
      }
    }
    setExceptions(await mealPlanExceptionsService.getAll());
  };

  const costAnalysis = useMemo(() => {
    if (!costSettings) return null;
    const plan = types.find((t) => t.active) || types[0];
    const mealsPerWeek = plan
      ? plan.periods.length * plan.daysOfWeek.length
      : 5;
    return computeMealPlanCostAnalysis({
      foodCostPerMealCents: costSettings.foodCostPerMealCents,
      laborCostPerMealCents: costSettings.laborCostPerMealCents,
      overheadPercent: costSettings.overheadPercent,
      planPriceCents: plan?.priceCents || 0,
      mealsPerWeek,
      bookedMeals: weekStats.booked + weekStats.redeemed + weekStats.noShow,
      noShowMeals: weekStats.noShow,
    });
  }, [costSettings, types, weekStats]);

  const upcomingWeekId = nextWeekId();
  const upcomingWeek = weeks.find((w) => w.id === upcomingWeekId);
  const hasActivePlan = types.some((t) => t.active);
  const menuPublished = upcomingWeek?.status === 'published' || upcomingWeek?.status === 'locked';
  const flowSteps: { id: typeof subTab; n: string; title: string; detail: string; done: boolean }[] = [
    {
      id: 'types',
      n: '1',
      title: 'Create the plan',
      detail: hasActivePlan ? 'A plan is ready to sell' : 'Add the name and weekly price',
      done: hasActivePlan,
    },
    {
      id: 'weeks',
      n: '2',
      title: 'Publish next week',
      detail: menuPublished
        ? `Week of ${formatWeekRange(upcomingWeekId)} is ${upcomingWeek?.status}`
        : 'Write the meals, save, then publish',
      done: menuPublished,
    },
    {
      id: 'subscribers',
      n: '3',
      title: 'See who is booked',
      detail: 'Customers pay on the phone. Staff sell on the till.',
      done: subs.some((s) => s.status === 'active' && s.coveredWeekId === upcomingWeekId),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg p-4 space-y-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Meal plans</h2>
          <p className="text-sm text-gray-600 mt-1">
            Do these in order each week. Publishing next week’s menu is what lets the till and the phone sell it.
            Collect on the till serves today’s plate. It does not take the weekly payment.
          </p>
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {flowSteps.map((step) => (
            <button
              key={step.id}
              type="button"
              onClick={() => {
                if (step.id === 'weeks') setSelectedWeekId(upcomingWeekId);
                setSubTab(step.id);
              }}
              className={`text-left rounded-lg border px-3 py-3 transition-colors ${
                subTab === step.id
                  ? 'border-pink-400 bg-pink-50'
                  : 'border-gray-200 bg-gray-50 hover:bg-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                    step.done ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {step.done ? '✓' : step.n}
                </span>
                <span className="font-semibold text-gray-900">{step.title}</span>
              </div>
              <p className="mt-1 text-xs text-gray-600 pl-8">{step.detail}</p>
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          <button
            type="button"
            onClick={() => setSubTab('exceptions')}
            className={`px-3 py-1 rounded-full ${
              subTab === 'exceptions' ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Exceptions
          </button>
          <button
            type="button"
            onClick={() => setSubTab('cost')}
            className={`px-3 py-1 rounded-full ${
              subTab === 'cost' ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Cost check
          </button>
        </div>
      </div>

      {subTab === 'weeks' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Step 2. Open <strong>Next week</strong>, add the breakfast, lunch, and dinner dishes, then{' '}
            <strong>Save menu</strong> and <strong>Publish</strong>. Customers and the till can buy that week only after it is published.
          </p>
          <div className="bg-white shadow rounded-lg p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="px-2 py-1 rounded border text-sm hover:bg-gray-50"
                  onClick={() => setSelectedWeekId(addDaysToDayKey(selectedWeekId, -7))}
                  title="Previous week"
                >
                  ←
                </button>
                <div>
                  <div className="text-base font-semibold text-gray-900">
                    Week of {formatWeekRange(selectedWeekId)}
                  </div>
                  <div className="text-xs text-gray-500">Monday {selectedWeekId}</div>
                  <p className="text-xs text-gray-600 mt-1">
                    Customers pay the weekly plan price, not these meals.
                  </p>
                </div>
                <button
                  type="button"
                  className="px-2 py-1 rounded border text-sm hover:bg-gray-50"
                  onClick={() => setSelectedWeekId(addDaysToDayKey(selectedWeekId, 7))}
                  title="Next week"
                >
                  →
                </button>
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                  onClick={() => setSelectedWeekId(weekIdContaining())}
                >
                  This week
                </button>
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200"
                  onClick={() => setSelectedWeekId(nextWeekId())}
                >
                  Next week
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {weekDoc && (
                  <span
                    className={`text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${statusBadgeClass(weekDoc.status)}`}
                  >
                    {weekDoc.status}
                  </span>
                )}
                {menuDirty && (
                  <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded">
                    Unsaved changes
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  Meals written {menuFillStats.filled}/{menuFillStats.total}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center border-t pt-3">
              <button
                type="button"
                disabled={busy || !weekDoc || weekReadOnly}
                className="text-sm px-3 py-1.5 rounded bg-pink-500 text-white disabled:opacity-40"
                onClick={() => void saveWeekMenu()}
              >
                Save menu
              </button>
              <button
                type="button"
                disabled={busy || !weekDoc || weekDoc.status === 'locked' || weekDoc.status === 'closed'}
                className="text-sm px-3 py-1.5 rounded bg-green-600 text-white disabled:opacity-40"
                onClick={() => void publishWeek()}
              >
                Publish
              </button>
              <button
                type="button"
                disabled={busy || !weekDoc || weekDoc.status === 'draft'}
                className="text-sm px-3 py-1.5 rounded bg-amber-600 text-white disabled:opacity-40"
                title="Stops customer cancellations for this week"
                onClick={() => void lockWeek()}
              >
                Lock cancellations
              </button>
              <button
                type="button"
                disabled={busy || weekReadOnly}
                className="text-sm px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40"
                onClick={() => void copyFromPrevious()}
              >
                Copy last week
              </button>
              <button
                type="button"
                disabled={busy || !weekDoc || weekDoc.status === 'draft'}
                className="text-sm px-3 py-1.5 rounded bg-indigo-600 text-white disabled:opacity-40"
                title="Creates the week’s meals for people who already paid"
                onClick={() => void runAutoBook()}
              >
                Fill paid bookings
              </button>
              {weekDoc && (
                <span className="text-xs text-gray-500 ml-auto">
                  Cancel cutoff for this week’s bookings:{' '}
                  <strong>{new Date(cancellationCutoffMs(weekDoc.id)).toLocaleString()}</strong>
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-3 items-center text-xs text-gray-600">
              <span className="font-medium">Meals this week:</span>
              {PERIODS.map((p) => (
                <label key={p} className="inline-flex items-center gap-1 capitalize">
                  <input
                    type="checkbox"
                    checked={offeredPeriods(weekDoc?.periods).includes(p)}
                    disabled={!weekDoc || weekReadOnly}
                    onChange={(e) => {
                      if (!weekDoc) return;
                      const current = offeredPeriods(weekDoc.periods);
                      const next = e.target.checked
                        ? PERIODS.filter((x) => current.includes(x) || x === p)
                        : current.filter((x) => x !== p);
                      if (!next.length) return;
                      setWeekDoc({ ...weekDoc, periods: next });
                      setMenuDirty(true);
                    }}
                  />
                  {p}
                </label>
              ))}
              <span className="text-gray-500">
                Only this week. Save menu so the phone hides a meal you turn off.
              </span>
            </div>
          </div>

          {busy && <p className="text-sm text-gray-500">Loading…</p>}

          {weekDoc && (
            <div className="space-y-3">
              {dayKeysForWeek(weekDoc.id).map((dayKey, dayIdx) => {
                const day = weekDoc.menu[dayKey];
                return (
                  <section key={dayKey} className="bg-white shadow rounded-lg p-4 space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-gray-900">{formatDayHeading(dayKey, dayIdx)}</h3>
                        <p className="text-[11px] text-gray-400">{dayKey}</p>
                      </div>
                      {!weekReadOnly && (
                        <div className="text-right">
                          <button
                            type="button"
                            className="text-xs font-semibold text-indigo-600 hover:underline"
                            onClick={() => {
                              if (!dayHasDishes(dayKey)) {
                                alert('That day has no meals to copy.');
                                return;
                              }
                              setCopyDayKey((current) => (current === dayKey ? null : dayKey));
                              setCopyDayTargets([]);
                            }}
                          >
                            Copy day
                          </button>
                          {copyDayKey === dayKey && (
                            <div className="mt-2 space-y-1 rounded-md border border-gray-200 bg-white p-2 text-xs text-left text-gray-700">
                              <p className="font-medium text-gray-900">Copy onto</p>
                              {dayKeysForWeek(weekDoc.id).filter((key) => key !== dayKey).map((key) => {
                                const index = dayKeysForWeek(weekDoc.id).indexOf(key);
                                return (
                                  <label key={key} className="flex items-center gap-1.5">
                                    <input
                                      type="checkbox"
                                      checked={copyDayTargets.includes(key)}
                                      onChange={(event) => {
                                        setCopyDayTargets((prev) =>
                                          event.target.checked ? [...prev, key] : prev.filter((row) => row !== key),
                                        );
                                      }}
                                    />
                                    {formatDayHeading(key, index)}
                                  </label>
                                );
                              })}
                              <div className="flex gap-2 pt-1">
                                <button
                                  type="button"
                                  disabled={copyDayTargets.length === 0}
                                  className="rounded bg-indigo-600 px-2 py-1 text-[11px] font-semibold text-white disabled:opacity-40"
                                  onClick={() => copyDayOnto(dayKey, copyDayTargets)}
                                >
                                  Apply
                                </button>
                                <button
                                  type="button"
                                  className="rounded border border-gray-300 px-2 py-1 text-[11px]"
                                  onClick={() => {
                                    setCopyDayKey(null);
                                    setCopyDayTargets([]);
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <label className="block">
                      <span className="text-xs font-medium text-gray-600">Day name</span>
                      <input
                        type="text"
                        value={day?.title || ''}
                        disabled={weekReadOnly}
                        placeholder="Comfort Meals"
                        onChange={(event) => patchDay(dayKey, { title: event.target.value })}
                        className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50"
                      />
                    </label>
                    {offeredPeriods(weekDoc.periods).map((period) => {
                      const textKey = `${period}Text` as const;
                      const value = day?.[textKey] ?? mealPeriodText(day, period);
                      return (
                        <label key={period} className="block">
                          <span className="text-xs font-medium text-gray-600 capitalize">{period}</span>
                          <textarea
                            value={value}
                            disabled={weekReadOnly}
                            rows={3}
                            placeholder={MEAL_PLACEHOLDER}
                            onChange={(event) => patchDay(dayKey, { [textKey]: event.target.value, [period]: [] })}
                            className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-50"
                          />
                          {!weekReadOnly && value.trim() && (
                            <button
                              type="button"
                              className="mt-1 text-[11px] text-indigo-600 hover:underline"
                              onClick={() => copyPeriodAcrossWeek(dayKey, period)}
                            >
                              Copy to all days
                            </button>
                          )}
                        </label>
                      );
                    })}
                  </section>
                );
              })}
              {weekReadOnly && (
                <p className="px-4 py-2 text-xs text-amber-800 bg-amber-50 rounded-lg">
                  This week is {weekDoc.status} — menu editing is disabled.
                </p>
              )}
            </div>
          )}

          <div className="bg-white shadow rounded-lg p-4">
            <h3 className="font-semibold mb-2">Kitchen prep (from bookings)</h3>
            <p className="text-xs text-gray-500 mb-2">
              Booked {weekStats.booked} · Cancelled {weekStats.cancelled} · Redeemed {weekStats.redeemed} ·
              No-show {weekStats.noShow} · Cancel rate {(weekStats.cancellationRate * 100).toFixed(0)}% ·
              No-show rate {(weekStats.noShowRate * 100).toFixed(0)}%
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Day</th>
                    <th className="px-2 py-1 text-left">Period</th>
                    <th className="px-2 py-1 text-left">Item</th>
                    <th className="px-2 py-1 text-right">Covers</th>
                  </tr>
                </thead>
                <tbody>
                  {prepRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-2 py-4 text-center text-gray-500">
                        No prep rows yet — publish, auto-book, or wait for subscriptions.
                      </td>
                    </tr>
                  )}
                  {prepRows.map((r) => (
                    <tr key={`${r.dayKey}-${r.period}-${r.menuItemId}`} className="border-t">
                      <td className="px-2 py-1">{r.dayKey}</td>
                      <td className="px-2 py-1 capitalize">{r.period}</td>
                      <td className="px-2 py-1">{r.menuItemName}</td>
                      <td className="px-2 py-1 text-right">{r.booked}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {subTab === 'types' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Step 1. This is the product staff and customers buy. One payment covers one week. Set the days, the meals, and the price.
          </p>
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Plans for sale</h3>
            <button
              type="button"
              className="bg-pink-500 text-white text-sm font-bold py-2 px-3 rounded-lg flex items-center"
              onClick={() => void openTypeDraft(emptyType())}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add plan type
            </button>
          </div>
          <div className="bg-white shadow rounded-lg overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Mode</th>
                  <th className="px-3 py-2 text-left">Periods</th>
                  <th className="px-3 py-2 text-left">Days</th>
                  <th className="px-3 py-2 text-left">Price</th>
                  <th className="px-3 py-2 text-left">T&C</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id} className="border-t">
                    <td className="px-3 py-2">{t.name}</td>
                    <td className="px-3 py-2">{t.bookingMode}</td>
                    <td className="px-3 py-2">{t.periods.join(', ')}</td>
                    <td className="px-3 py-2">
                      {t.daysOfWeek.map((d) => WEEKDAYS.find((w) => w.n === d)?.label).join(' ')}
                    </td>
                    <td className="px-3 py-2">${dollars(t.priceCents)}</td>
                    <td className="px-3 py-2">{t.tcVersionRef}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="text-pink-600"
                        onClick={() => void openTypeDraft({ ...t })}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {typeDraft && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-3">
                <h3 className="font-bold text-lg">{typeDraft.id ? 'Edit plan type' : 'New plan type'}</h3>
                <input
                  className="w-full border rounded px-3 py-2"
                  placeholder="Name"
                  value={typeDraft.name}
                  onChange={(e) => setTypeDraft({ ...typeDraft, name: e.target.value })}
                />
                <textarea
                  className="w-full border rounded px-3 py-2"
                  placeholder="Description"
                  value={typeDraft.description}
                  onChange={(e) => setTypeDraft({ ...typeDraft, description: e.target.value })}
                />
                <label className="block text-sm">
                  Booking mode
                  <select
                    className="ml-2 border rounded px-2 py-1"
                    value={typeDraft.bookingMode}
                    onChange={(e) =>
                      setTypeDraft({
                        ...typeDraft,
                        bookingMode: e.target.value === 'manual' ? 'manual' : 'auto',
                      })
                    }
                  >
                    <option value="auto">Auto-book (opt-out)</option>
                    <option value="manual">Manual (opt-in)</option>
                  </select>
                </label>
                <div className="text-sm">
                  Periods:{' '}
                  {PERIODS.map((p) => (
                    <label key={p} className="mr-3 inline-flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={typeDraft.periods.includes(p)}
                        onChange={(e) => {
                          const periods = e.target.checked
                            ? [...typeDraft.periods, p]
                            : typeDraft.periods.filter((x) => x !== p);
                          setTypeDraft({ ...typeDraft, periods: periods.length ? periods : ['lunch'] });
                        }}
                      />
                      {p}
                    </label>
                  ))}
                </div>
                <div className="text-sm">
                  Days:{' '}
                  {WEEKDAYS.map((d) => (
                    <label key={d.n} className="mr-2 inline-flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={typeDraft.daysOfWeek.includes(d.n)}
                        onChange={(e) => {
                          const daysOfWeek = e.target.checked
                            ? [...typeDraft.daysOfWeek, d.n].sort()
                            : typeDraft.daysOfWeek.filter((x) => x !== d.n);
                          setTypeDraft({
                            ...typeDraft,
                            daysOfWeek: daysOfWeek.length ? daysOfWeek : [1],
                          });
                        }}
                      />
                      {d.label}
                    </label>
                  ))}
                </div>
                <label className="block text-sm">
                  Price ($)
                  <input
                    className="ml-2 border rounded px-2 py-1"
                    value={priceText}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d*\.?\d*$/.test(v)) setPriceText(v);
                    }}
                  />
                </label>
                <label className="block text-sm">
                  T&C version
                  <input
                    className="ml-2 border rounded px-2 py-1"
                    value={typeDraft.tcVersionRef}
                    onChange={(e) => {
                      const tcVersionRef = e.target.value;
                      setTypeDraft({ ...typeDraft, tcVersionRef });
                    }}
                    onBlur={() => void loadTcBodyForVersion(typeDraft.tcVersionRef)}
                  />
                </label>
                <label className="block text-sm">
                  T&C text (shown in a popup on mobile &amp; online)
                  <textarea
                    className="mt-1 w-full border rounded px-3 py-2 text-sm min-h-[180px] font-mono"
                    value={tcBodyDraft}
                    disabled={tcBodyLoading}
                    onChange={(e) => setTcBodyDraft(e.target.value)}
                    placeholder="Full terms students see in the View terms modal"
                  />
                </label>
                <p className="text-xs text-gray-500">
                  Bumping the version (e.g. 2026-v2) forces students to accept again. Edit the text for that
                  version above — it is what opens in the popup modal.
                </p>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={typeDraft.active}
                    onChange={(e) => setTypeDraft({ ...typeDraft, active: e.target.checked })}
                  />
                  Active
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" className="px-3 py-1.5 rounded border" onClick={() => setTypeDraft(null)}>
                    Cancel
                  </button>
                  <button type="button" className="px-3 py-1.5 rounded bg-pink-500 text-white" onClick={() => void saveType()}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {subTab === 'subscribers' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Step 3. People already booked. To sell a week, use <strong>MEAL PLN</strong> on the till, or the customer pays on the phone or website.
          </p>
          <MealPlanNewCustomerForm
            onCreated={(customer) => {
              setAddedCustomers((prev) => [...prev.filter((c) => c.id !== customer.id), customer]);
              onCustomerCreated?.(customer);
            }}
          />
          {allowManualGrant && (
          <div className="bg-white shadow rounded-lg p-4 space-y-3">
            <h3 className="font-semibold">Manual grant (pilot)</h3>
            <input
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Search customer…"
              value={lookupQuery}
              onChange={(e) => setLookupQuery(e.target.value)}
            />
            {filteredCustomers.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`block w-full text-left text-sm px-2 py-1 rounded ${
                  grantCustomerId === String(c.id) ? 'bg-pink-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => setGrantCustomerId(String(c.id))}
              >
                {c.name} · {c.email}
              </button>
            ))}
            <select
              className="border rounded px-2 py-1 text-sm"
              value={grantPlanTypeId}
              onChange={(e) => setGrantPlanTypeId(e.target.value)}
            >
              <option value="">Select plan type</option>
              {types.filter((t) => t.active).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <button type="button" className="bg-pink-500 text-white text-sm px-3 py-1.5 rounded" onClick={() => void grantSubscription()}>
              Grant subscription
            </button>
          </div>
          )}
          <div className="bg-white shadow rounded-lg overflow-x-auto">
            <div className="p-3 border-b">
              <input
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Search by name, plan, or week"
                value={bookedQuery}
                onChange={(e) => setBookedQuery(e.target.value)}
              />
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left">Week</th>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-right">Collected</th>
                  <th className="px-3 py-2 text-right">Remaining</th>
                  <th className="px-3 py-2 text-left">Record</th>
                </tr>
              </thead>
              <tbody>
                {subs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                      Nobody is booked yet.
                    </td>
                  </tr>
                )}
                {subs
                  .map((s) => {
                    const customer = knownCustomers.find((c) => String(c.id) === String(s.customerId));
                    const plan = types.find((t) => t.id === s.planTypeId);
                    const meals = subscriberBookings.filter((b) =>
                      b.subscriptionId
                        ? b.subscriptionId === s.id
                        : String(b.customerId) === String(s.customerId) && b.weekId === s.coveredWeekId,
                    );
                    const collected = meals.filter((b) => b.status === 'redeemed').length;
                    const remaining = meals.filter((b) => b.status === 'booked').length;
                    const cancelled = meals.filter((b) => b.status === 'cancelled').length;
                    const noShow = meals.filter((b) => b.status === 'no_show').length;
                    const name = customer?.name || s.customerEmail || 'Unknown customer';
                    const weekLabel = s.coveredWeekId ? formatWeekRange(s.coveredWeekId) : 'Week not set';
                    return { s, customer, plan, meals, collected, remaining, cancelled, noShow, name, weekLabel };
                  })
                  .filter((row) => {
                    const q = bookedQuery.trim().toLowerCase();
                    if (!q) return true;
                    return `${row.name} ${row.customer?.email || ''} ${row.customer?.phone || ''} ${row.s.planTypeName || ''} ${row.weekLabel} ${row.s.coveredWeekId || ''}`
                      .toLowerCase()
                      .includes(q);
                  })
                  .map((row) => (
                    <tr key={row.s.id} className="border-t align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{row.name}</div>
                        <div className="text-xs text-gray-500">
                          {row.customer?.phone || 'No phone'}
                          {row.customer?.email || row.s.customerEmail
                            ? ` · ${row.customer?.email || row.s.customerEmail}`
                            : ''}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div>{row.weekLabel}</div>
                        {row.s.coveredWeekId && (
                          <div className="text-xs text-gray-500">Starts {row.s.coveredWeekId}</div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div>{row.s.planTypeName || row.plan?.name || row.s.planTypeId}</div>
                        {row.plan && <div className="text-xs text-gray-500">{planScheduleLabel(row.plan)}</div>}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{row.collected}</td>
                      <td className="px-3 py-2 text-right font-medium">{row.remaining}</td>
                      <td className="px-3 py-2 text-xs text-gray-700 space-y-1">
                        <div className="capitalize">{row.s.status}</div>
                        <div>Booked {formatRecordedAt(row.s.createdAt)}</div>
                        <div>{paymentSourceLabel(row.s.paymentSource)}</div>
                        {row.s.status === 'cancelled' && (
                          <div>Plan cancelled {formatRecordedAt(row.s.cancelledAt || row.s.updatedAt)}</div>
                        )}
                        {row.meals
                          .slice()
                          .sort((a, b) => a.dayKey.localeCompare(b.dayKey) || a.period.localeCompare(b.period))
                          .map((meal) => {
                            const when =
                              meal.status === 'redeemed'
                                ? `Picked up ${formatRecordedAt(meal.redeemedAt || meal.updatedAt)}`
                                : meal.status === 'cancelled'
                                  ? `Cancelled ${formatRecordedAt(meal.cancelledAt || meal.updatedAt)}`
                                  : meal.status === 'no_show'
                                    ? `No-show ${formatRecordedAt(meal.updatedAt)}`
                                    : `Still to collect · booked ${formatRecordedAt(meal.createdAt)}`;
                            return (
                              <div key={meal.id}>
                                {bookingDayLabel(meal.dayKey)} {meal.period} · {when}
                              </div>
                            );
                          })}
                        {row.customer?.studentVerified && <div>Student ID checked</div>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {subTab === 'exceptions' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Students ask to move unused meals to next week. Nothing changes until a manager approves.
          </p>
          {exceptions.length === 0 && (
            <p className="text-sm text-gray-500">No exception requests yet.</p>
          )}
          {exceptions.map((req) => {
            const history = exceptions.filter(
              (e) =>
                e.customerId === req.customerId &&
                e.createdAt >= Date.now() - 60 * 86400000,
            );
            const flag =
              history.length >= 4 ? 'red' : history.length >= 2 ? 'amber' : 'green';
            return (
              <div key={req.id} className="bg-white shadow rounded-lg p-4 space-y-2">
                <div className="flex justify-between gap-2">
                  <div>
                    <div className="font-medium">
                      {req.customerName || req.customerEmail} · {req.mealCount} meal(s)
                    </div>
                    <div className="text-sm text-gray-600">
                      {req.fromWeekId} → {req.toWeekId} · {req.status}
                    </div>
                    <div className="text-sm mt-1">{req.reason}</div>
                  </div>
                  <span
                    className={`text-xs px-2 py-1 rounded h-fit ${
                      flag === 'red'
                        ? 'bg-red-100 text-red-800'
                        : flag === 'amber'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-green-100 text-green-800'
                    }`}
                  >
                    {history.length} req / 60d
                  </span>
                </div>
                {history.length > 0 && (
                  <p className="text-xs text-gray-500">
                    History:{' '}
                    {history
                      .map(
                        (h) =>
                          `${new Date(h.createdAt).toLocaleDateString()} ${h.status}${
                            h.managerComment ? ` — ${h.managerComment}` : ''
                          }`,
                      )
                      .join('; ')}
                  </p>
                )}
                {req.status === 'pending' && isManager && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="text-sm px-3 py-1 rounded bg-green-600 text-white"
                      onClick={() => {
                        const comment = window.prompt('Comment (saved permanently)') || '';
                        void resolveException(req, 'approved', comment);
                      }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="text-sm px-3 py-1 rounded bg-gray-600 text-white"
                      onClick={() => {
                        const comment = window.prompt('Comment (saved permanently)') || '';
                        void resolveException(req, 'denied', comment);
                      }}
                    >
                      Deny
                    </button>
                  </div>
                )}
                {req.managerComment && (
                  <p className="text-xs text-gray-600">
                    Manager: {req.managerName} — {req.managerComment}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {subTab === 'cost' && costSettings && costAnalysis && (
        <div className="bg-white shadow rounded-lg p-4 space-y-3 max-w-lg">
          <h3 className="font-semibold">Cost check</h3>
          <p className="text-sm text-gray-600">
            Rough food, labour, and overhead against the plan price. This does not change bookings or payments.
          </p>
          <label className="block text-sm">
            Food cost / meal ($)
            <input
              className="ml-2 border rounded px-2 py-1"
              value={dollars(costSettings.foodCostPerMealCents)}
              onChange={(e) =>
                setCostSettings({
                  ...costSettings,
                  foodCostPerMealCents: Math.round((parseFloat(e.target.value) || 0) * 100),
                })
              }
            />
          </label>
          <label className="block text-sm">
            Labor cost / meal ($)
            <input
              className="ml-2 border rounded px-2 py-1"
              value={dollars(costSettings.laborCostPerMealCents)}
              onChange={(e) =>
                setCostSettings({
                  ...costSettings,
                  laborCostPerMealCents: Math.round((parseFloat(e.target.value) || 0) * 100),
                })
              }
            />
          </label>
          <label className="block text-sm">
            Overhead %
            <input
              className="ml-2 border rounded px-2 py-1"
              value={String(costSettings.overheadPercent)}
              onChange={(e) =>
                setCostSettings({
                  ...costSettings,
                  overheadPercent: parseFloat(e.target.value) || 0,
                })
              }
            />
          </label>
          <button
            type="button"
            className="bg-pink-500 text-white text-sm px-3 py-1.5 rounded"
            onClick={async () => {
              const saved = await mealPlanCostSettingsService.save({
                ...costSettings,
                updatedBy: currentStaffName,
              });
              setCostSettings(saved);
              alert('Saved.');
            }}
          >
            Save inputs
          </button>
          <div className="text-sm space-y-1 border-t pt-3">
            <div>Cost / meal: ${dollars(costAnalysis.costPerMealCents)}</div>
            <div>Effective price / meal: ${dollars(costAnalysis.effectivePricePerMealCents)}</div>
            <div>Margin / meal: ${dollars(costAnalysis.marginPerMealCents)}</div>
            <div>Break-even uptake: {(costAnalysis.breakEvenUptake * 100).toFixed(0)}%</div>
            <div>Weekly food cost (booked): ${dollars(costAnalysis.weeklyFoodCostCents)}</div>
            <div>Waste (no-shows): ${dollars(costAnalysis.wasteCostCents)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPlansTab;
